import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/articleTypes';
import type { ArticleSection } from '@/mocks/articleTypes';

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

function renderSection(section: ArticleSection, index: number) {
  switch (section.type) {
    case 'heading':
      if (section.level === 2) {
        return (
          <h2 key={index} className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-10 mb-4">
            {section.content}
          </h2>
        );
      }
      return (
        <h3 key={index} className="font-heading text-lg font-semibold text-foreground-900 mt-8 mb-3">
          {section.content}
        </h3>
      );

    case 'paragraph':
      return (
        <p key={index} className="text-foreground-700 leading-relaxed mb-4">
          {section.content}
        </p>
      );

    case 'list':
      return (
        <ul key={index} className="space-y-2 mb-5 ml-1">
          {section.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-foreground-700 leading-relaxed">
              <span className="mt-1.5 w-1.5 h-1.5 flex-shrink-0 rounded-full bg-primary-400"></span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'tip':
      return (
        <div key={index} className="bg-accent-100/60 border-l-4 border-accent-500 rounded-r-lg p-4 md:p-5 my-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-flash-line text-accent-600 text-lg"></i>
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong className="text-foreground-900">温馨提示：</strong>{item.content}
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-3">
              文章未找到
            </h1>
            <p className="text-foreground-500 mb-8">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const categoryName = getCategoryName(article.category);

  const handleArticleClick = (articleSlug: string) => {
    navigate(`/article/${articleSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <title>{`${article.title} - 少妇护肤美容指南`}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <meta name="author" content={article.author} />
      <link rel="canonical" href={`https://meirong01.github.io/article/${slug}`} />
      <meta property="og:title" content={`${article.title} - 少妇护肤美容指南`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://meirong01.github.io/article/${slug}`} />
      <meta property="article:published_time" content={article.date} />
      <meta property="article:author" content={article.author} />
      {article.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} - 少妇护肤美容指南`} />
      <meta name="twitter:description" content={article.excerpt} />

      <Navbar />

      <main className="min-h-screen pt-20">
        <article className="w-full max-w-3xl mx-auto px-4 md:px-6 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: categoryName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
              {categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full aspect-video object-cover"
              title={article.title}
            />
          </div>

          <div className="prose-custom">
            {article.content.map((section, index) => renderSection(section, index))}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <Link
                to={`/category/${article.category}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回{categoryName}分类
              </Link>
              <div className="flex items-center gap-1 text-sm text-foreground-500">
                <span>分享：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-weibo-line"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-18">
            <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <div
                    key={ra.slug}
                    onClick={() => handleArticleClick(ra.slug)}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{ra.readTime}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}